<template>
  <footer id="site-footer" class="footer relative z-footer">
    <marquee
      class="absolute bottom-full left-0 w-full -mb-px text-midnight-blue"
      speed="16"
      fixed-speed
      reverse
    >
      <wave class="h-8 xs:h-10 md:h-16" />
    </marquee>
    <color-section
      :background-colors="getColor('midnight-blue')"
      :text-color="getColor('white')"
      :color-margin="
        `0px 0px ${
          winHeight === '100vh' ? '-100%' : `-${winHeight - headerHeight}px`
        } 0px`
      "
      class="footer__menu container scheme-dark text-scheme"
    >
      <div
        v-if="filteredProductMenu && filteredProductMenu.length > 0"
        class="footer__section footer__section--links"
      >
        <h3 class="footer__title">
          shop
        </h3>
        <ul class="footer__list">
          <li
            v-for="doc in filteredProductMenu"
            :key="doc._id"
            class="footer__item"
          >
            <recess-link :to="documentPath(doc)" class="footer__link link">
              {{ doc.title }}
            </recess-link>
          </li>
        </ul>
      </div>
      <div
        v-if="
          primaryMenu && filteredPrimaryMenu && filteredPrimaryMenu.length > 0
        "
        class="footer__section footer__section--links"
      >
        <h3 v-if="primaryMenu.title" class="footer__title">
          {{ primaryMenu.title }}
        </h3>
        <ul class="footer__list">
          <li
            v-for="link in filteredPrimaryMenu"
            :key="link._key"
            class="footer__item"
          >
            <recess-link :sanity-link="link" class="footer__link" />
          </li>
        </ul>
      </div>
      <div
        v-if="socialLinks.length > 0"
        class="footer__section footer__section--social md:hidden"
      >
        <recess-image :src="logo" alt="Recess" class="w-20 -mt-4 mb-2em" />
        <ul class="flex footer__list">
          <li
            v-for="link in socialLinks"
            :key="link._key"
            class="flex-initial mr-2em"
          >
            <a
              :href="link.url"
              target="_blank"
              rel="noopener"
              class="footer__link link"
            >
              <span class="font-blank">{{ link.name }}</span>
              <fa
                :icon="['fab', link.name.toLowerCase()]"
                class="footer__icon"
              />
            </a>
          </li>
        </ul>
      </div>
      <div
        v-if="
          secondaryMenu &&
            filteredSecondaryMenu &&
            filteredSecondaryMenu.length > 0
        "
        class="footer__section footer__section--links footer__section--contact"
      >
        <h3 v-if="secondaryMenu.title" class="footer__title">
          {{ secondaryMenu.title }}
        </h3>
        <ul class="footer__list">
          <li
            v-for="link in filteredSecondaryMenu"
            :key="link._key"
            class="footer__item"
          >
            <recess-link :sanity-link="link" class="footer__link" />
          </li>
        </ul>
      </div>
      <div class="footer__section footer__section--sign-up">
        <h3 class="footer__title">
          newsletter
        </h3>
        <p v-if="footerSettings.newsletterText">
          {{ footerSettings.newsletterText }}
        </p>
        <p v-else>
          Join our newsletter for work tips, featured interviews and deals on
          our latest products.
        </p>
        <sign-up class="footer__sign-up" />
      </div>
      <div class="footer__section footer__section--logo hidden md:block">
        <recess-image :src="logo" alt="Recess" class="w-24" />
      </div>
      <div
        v-if="!nonCBDSite && socialLinks.length > 0"
        class="items-end hidden footer__section footer__section--social md:flex"
      >
        <ul class="footer__list flex items-end">
          <li
            v-for="link in socialLinks"
            :key="link._key"
            class="flex-initial mr-2em"
          >
            <a
              :href="link.url"
              target="_blank"
              rel="noopener"
              class="footer__link link"
            >
              <span class="font-blank">{{ link.name }}</span>
              <fa
                :icon="['fab', link.name.toLowerCase()]"
                class="footer__icon"
              />
            </a>
          </li>
        </ul>
      </div>
      <div class="footer__section footer__section--fda-disclaimer">
        <div v-if="footerSettings.fdaDisclaimer">
          {{ footerSettings.fdaDisclaimer }}
        </div>
      </div>
      <div class="w-full flex flex-wrap">
        <div
          v-if="
            footerSettings.disclaimer ||
              (legalPageMenu &&
                legalPageMenu.links &&
                legalPageMenu.links.length > 0)
          "
          class="footer__section footer__section--legal"
        >
          <ul
            v-if="
              legalPageMenu &&
                legalPageMenu.links &&
                legalPageMenu.links.length > 0
            "
            class="footer__list flex mb-2em"
          >
            <li
              v-for="link in legalPageMenu.links"
              :key="link._key"
              class="footer__item flex-initial mr-2em"
            >
              <recess-link :sanity-link="link" class="footer__link" />
            </li>
          </ul>
          <p v-if="footerSettings.disclaimer">
            {{ footerSettings.disclaimer }}
          </p>
        </div>
        <div class="footer__section footer__section--colophon order-first">
          <p v-if="footerSettings.copyright">
            Branding &amp; website by
            <a
              href="https://dayjob.work"
              target="_blank"
              rel="noopener"
              class="link"
              ><span class="font-blank">Day Job</span
              ><svg
                width="70"
                height="20"
                viewBox="0 0 70 20"
                class="inline-block h-2em relative top-1/4em"
                preserveAspectRatio="xMinYMax meet"
                aria-hidden="true"
              >
                <path
                  d="M0 15.3v-.4h.5c.6 0 1.1-.2 1.3-.6.2-.3.3-.8.3-1.6V3.3c0-.9-.1-1.5-.3-1.7-.3-.3-.7-.5-1.3-.5H0V.7h5.9c2.2 0 3.8.2 5 .7s2 1.3 2.7 2.5 1 2.5 1 4c0 2-.6 3.7-1.8 5.1-1.4 1.5-3.5 2.3-6.3 2.3H0zm4.1-1.1c.9.2 1.7.3 2.3.3 1.7 0 3-.6 4.1-1.7S12.2 10 12.2 8s-.5-3.6-1.6-4.8-2.5-1.7-4.3-1.7c-.6 0-1.3.1-2.2.3v12.4zM21.7 13.8c-1 .8-1.6 1.2-1.9 1.4-.4.2-.8.3-1.2.3-.7 0-1.2-.2-1.7-.7-.4-.5-.7-1.1-.7-1.8 0-.5.1-.9.3-1.2.3-.5.8-.9 1.5-1.4.7-.4 1.9-1 3.6-1.6v-.4c0-1-.2-1.7-.5-2-.1-.4-.6-.6-1.2-.6-.5 0-.8.1-1.1.4-.3.2-.4.5-.4.8v.6c0 .3-.1.6-.3.8-.2.2-.4.3-.7.3-.3 0-.5-.1-.6-.3-.2-.2-.3-.4-.3-.8 0-.6.3-1.2.9-1.7s1.5-.8 2.6-.8c.9 0 1.6.1 2.1.4.4.2.7.6.9 1 .1.3.2.9.2 1.9v3.3c0 .9 0 1.5.1 1.7 0 .2.1.4.2.4.1.1.2.1.3.1.1 0 .2 0 .3-.1.2-.1.4-.4.9-.8v.6c-.8 1.1-1.6 1.6-2.3 1.6-.4 0-.6-.1-.8-.4 0 0-.2-.4-.2-1zm0-.7V9.4c-1.1.4-1.8.7-2.1.9-.6.3-1 .6-1.2 1-.2.3-.4.7-.4 1.1 0 .5.2.9.5 1.3s.7.5 1.1.5c.5 0 1.2-.3 2.1-1.1zM25.4 5.4H30v.4h-.2c-.3 0-.6.1-.7.2-.2.1-.2.3-.2.5 0 .3.1.7.4 1.2l2.4 5 2.2-5.4c.1-.3.2-.6.2-.9 0-.1 0-.2-.1-.3-.1-.1-.1-.1-.3-.2-.1 0-.3-.1-.6-.1v-.4h3.2v.4c-.3 0-.5.1-.6.2s-.3.2-.5.5c-.1.1-.2.4-.4.8l-4 9.8c-.4 1-.9 1.7-1.5 2.2s-1.2.7-1.8.7c-.4 0-.8-.1-1-.4-.3-.2-.4-.5-.4-.8s.1-.5.3-.7c.2-.2.5-.3.8-.3.2 0 .6.1 1 .2.3.1.5.2.5.2.2 0 .4-.1.7-.3.3-.2.5-.7.8-1.3l.7-1.7-3.5-7.4c-.4-.3-.6-.5-.8-.9-.2-.3-.3-.4-.4-.5-.2-.1-.5-.2-.8-.3v-.4zM43.9 1.1V.7h6.2v.4h-.5c-.6 0-1 .2-1.3.5-.2.3-.3.8-.3 1.7v7.1c0 1.1-.1 2-.4 2.7-.2.7-.7 1.3-1.2 1.8-.6.5-1.3.8-2.1.8-.7 0-1.2-.2-1.6-.5s-.6-.7-.6-1.1c0-.4.1-.6.3-.8s.5-.3.8-.3c.2 0 .4.1.6.2.2.2.4.6.7 1.2.2.4.4.6.6.6.2 0 .4-.1.5-.4s.3-.7.3-1.3v-10c0-.7 0-1.2-.1-1.5-.1-.2-.2-.3-.5-.5s-.6-.3-1-.3h-.4v.1zM55 5.1c1.5 0 2.7.6 3.6 1.7.8 1 1.1 2.1 1.1 3.3 0 .9-.2 1.8-.6 2.7-.4.9-1 1.6-1.7 2-.7.5-1.6.7-2.5.7-1.5 0-2.7-.6-3.5-1.8-.7-1-1.1-2.1-1.1-3.3 0-.9.2-1.8.7-2.7.4-.9 1-1.5 1.8-2 .6-.3 1.4-.6 2.2-.6zm-.3.7c-.4 0-.8.1-1.1.3-.4.2-.7.6-.9 1.2s-.4 1.3-.4 2.2c0 1.4.3 2.7.9 3.7s1.3 1.6 2.2 1.6c.7 0 1.3-.3 1.7-.9.5-.6.7-1.6.7-3 0-1.7-.4-3.1-1.1-4.1-.6-.6-1.2-1-2-1zM63.2 7.1c1-1.3 2-2 3.1-2 1 0 1.9.4 2.6 1.3S70 8.5 70 10c0 1.7-.6 3.2-1.7 4.2-1 .9-2.1 1.4-3.3 1.4-.6 0-1.2-.1-1.7-.3-.6-.2-1.2-.5-1.8-.9V4.1c0-1.1 0-1.8-.1-2.1s-.1-.4-.3-.5c-.1-.1-.3-.1-.4-.1-.2 0-.5.1-.8.2l-.2-.4 3-1.2h.5v7.1zm0 .7v5.9c.4.4.7.6 1.1.8s.8.3 1.2.3c.7 0 1.3-.4 1.8-1.1.6-.7.8-1.8.8-3.1s-.3-2.2-.8-2.9-1.2-1-1.9-1c-.4 0-.8.1-1.1.3-.3.1-.7.4-1.1.8z"
                /></svg></a
            ><br />
            Made in LA &amp; NYC<br />
            {{ footerSettings.copyright }}
            <span class="block mt-2em">
              RECESS&reg;, RECESS MOOD&reg;, RECESS ZERO PROOF&trade;, CALM COOL
              COLLECTED&reg;, AN ANTIDOTE TO MODERN TIMES&trade;, TAKE A
              RECESS&reg; and A DRINK FOR WHEN YOU DON&apos;T WANT ONE&reg; are
              trademarks of Drink Recess Inc.
            </span>
            <span class="block">
              Magtein&reg; is protected under a family of US patents, pending
              patents, and is protected worldwide. Magtein&reg; is a trademark
              of Magceutics&reg;, Inc. and is distributed exclusively by AIDP,
              Inc.
            </span>
          </p>
        </div>
      </div>
    </color-section>
  </footer>
</template>

<script>
import { mapState } from 'vuex'
import Wave from '~/components/wave.vue'
import SignUp from '~/components/sign-up.vue'
import logo from '~/assets/images/logo.svg'
import getCBDFilter from '~/util/get-cbd-filter'

export default {
  name: 'SiteFooter',

  components: {
    Wave,
    SignUp
  },

  data: () => ({
    nonCBDSite: process.env.nonCBDSite,
    isCBDSafe: getCBDFilter(process.env.nonCBDSite)
  }),

  computed: {
    ...mapState({
      settings: state => state.settings,
      overlay: state => state.screen.overlay,
      winHeight: state => state.screen.height,
      headerHeight: state => state.screen.headerHeight,
      primaryMenu: state => state.primaryMenu,
      secondaryMenu: state => state.secondaryMenu,
      productMenu: state => state.shop.productMenu,
      legalPageMenu: state => state.legalPageMenu
    }),
    filteredProductMenu() {
      return this.productMenu.filter(this.isCBDSafe)
    },
    filteredPrimaryMenu() {
      return this.primaryMenu.links.filter(this.isCBDSafe)
    },
    filteredSecondaryMenu() {
      return this.secondaryMenu.links.filter(this.isCBDSafe)
    },
    footerSettings() {
      const settings = this.settings
      if (settings) {
        return settings.footer || {}
      } else {
        return {}
      }
    },
    socialLinks() {
      const settings = this.settings
      let links = []
      if (settings) {
        const socialMedia = settings.socialMedia
        if (socialMedia) {
          links = socialMedia.links
        }
      }
      return links
    },
    logo() {
      return logo
    }
  }
}
</script>

<style>
.footer {
  background-color: theme('backgroundColor.midnight-blue');
  color: theme('colors.white');
  font-size: theme('fontSize.sm');
  transform: translateZ(0); /* prevents clipping with clouds etc. */
}

.footer__menu {
  display: flex;
  flex-wrap: wrap;
  line-height: theme('lineHeight.snug');
  padding-top: var(--y-gutter);
}

.footer__section {
  flex: 1 1 auto;
  margin-bottom: var(--y-gutter);
  width: 100%;
}

.footer__section--links,
.footer__section--social {
  padding-right: var(--x-gutter);
  width: 50%;
}

.footer__title {
  font-weight: theme('fontWeight.medium');
  margin-bottom: 1em;
}

.footer__item {
  margin-bottom: 0.5em;
}

.footer__section--sign-up,
.footer__section--legal,
.footer__section--colophon,
.footer__section--fda-disclaimer {
  flex: 0 1 auto;
  line-height: theme('lineHeight.relaxed');
}

.footer__section--social,
.footer__section--sign-up {
  font-size: theme('fontSize.lg');
}

.footer__sign-up.sign-up {
  margin-top: 1.5em;
}

.footer__section--logo,
.footer__section--legal {
  margin-bottom: var(--y-gutter-sm);
}

.footer__section--legal,
.footer__section--colophon {
  color: theme('colors.waterloo');
  font-size: theme('fontSize.sm');
}

.footer__section--colophon {
  margin-bottom: var(--y-gutter-sm);
}

.footer__section--fda-disclaimer {
  border: 1px solid white;
  flex-grow: 0;
  font-size: theme('fontSize.sm');
  margin-left: auto;
  padding: 2em;
}

@screen xs {
  .footer__title {
    font-size: theme('fontSize.lg');
  }
}

@screen sm {
  .footer__section {
    padding-right: var(--x-gutter-sm);
    width: 50%;
  }

  .footer__section--contact .footer__list {
    column-gap: 1em;
    columns: 2;
  }

  .footer__section--sign-up {
    width: 100%;
  }
}

@screen md {
  .footer__section {
    margin-bottom: var(--y-gutter-lg);
    width: 50%;
  }

  .footer__section--links {
    width: 33.3%;
  }

  .footer__section--sign-up {
    width: 75%;
  }

  .footer__section--logo,
  .footer__section--social {
    margin-bottom: var(--y-gutter);
  }

  .footer__section--logo,
  .footer__section--colophon {
    width: 40%;
  }

  .footer__section--social {
    font-size: theme('fontSize.2xl');
  }

  .footer__section--legal,
  .footer__section--social,
  .footer__section--fda-disclaimer {
    width: 60%;
  }
}

@screen xl {
  .footer__section--links {
    width: 20%;
  }

  .footer__section--contact .footer__list {
    column-gap: normal;
    columns: auto;
  }

  .footer__section--sign-up {
    width: 40%;
  }
}
</style>
